<template>
  <ViewMode
    v-if="mode === 'view'"
    :custom-field="customField"
    :value="localValue"
    :is-revealed="isRevealed"
    @toggle-sensitive="toggleSensitive"
  />

  <EditMode
    v-else-if="mode === 'edit'"
    v-model="localValue"
    :custom-field="customField"
    :is-revealed="isRevealed"
    @reveal="revealSensitive"
    @hide="hide"
  />
</template>

<script setup lang="ts">
import { ref, watch, onMounted } from 'vue';
import type { CustomFieldEntity } from '@/api/entity_types/CustomField';
import type { CustomFieldableType, CustomFieldValueEntity } from '@/api/entity_types/CustomFieldValue';
import ViewMode from './ViewMode.vue';
import EditMode from './EditMode.vue';
import type { CustomFieldValueUpdatedPayload } from './types';
import { useSensitiveField } from './useSensitiveField';

type Props = {
  customField: CustomFieldEntity;
  customFieldValue?: CustomFieldValueEntity & { custom_field: CustomFieldEntity };
  customFieldableType: CustomFieldableType;
  customFieldableId: number;
  mode: 'view' | 'edit';
};

const props = withDefaults(defineProps<Props>(), {
  customFieldValue: undefined,
});

const emit = defineEmits<{
  updated: [payload: CustomFieldValueUpdatedPayload];
}>();

const { isRevealed, reveal, hide, reset } = useSensitiveField();

const localValue = ref<string | string[]>('');

const syncValueFromProp = () => {
  const val = props.customFieldValue?.value || '';
  localValue.value = props.customField.field_type === 'multiple_options' ? (val ? val.split(',') : []) : val;
};

onMounted(syncValueFromProp);

watch(
  () => props.customFieldValue?.value,
  () => {
    syncValueFromProp();
    if (props.customField.sensitive) reset();
  }
);

watch(localValue, newVal => {
  if (props.mode !== 'edit') return;

  emit('updated', {
    custom_field_value: {
      key: props.customField.name,
      custom_fieldable_type: props.customFieldableType,
      custom_fieldable_id: props.customFieldableId,
      custom_field_id: props.customField.id,
      value:
        props.customField.field_type === 'multiple_options' && Array.isArray(newVal)
          ? newVal.length
            ? newVal.join(',')
            : ''
          : [newVal].flat().join(','),
    },
  });
});

const revealSensitive = async () => {
  const decryptedValue = await reveal(props.customFieldValue?.id);
  localValue.value = decryptedValue;
};

const toggleSensitive = async () => {
  if (isRevealed.value) {
    hide();
  } else {
    await revealSensitive();
  }
};
</script>
